import React, { useMemo } from "react";
import { Typography, Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getGrantInfo, getTooltipMsgs } from "selectors/energyAssistance.selectors";
import { useSelector } from "react-redux";
import { InfoTooltip } from "_components";
import dayjs from "dayjs";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { initialState } from "container/energyAssistance/store/initialState";
import { API_LOAD_STATUS } from "constants/labels";

const GrantInformationAndHistory = () => {
  const grantInfo = useSelector(getGrantInfo);
  const tooltipMsgs = useSelector(getTooltipMsgs);
  const getTooltip = (title) =>
    tooltipMsgs?.Data?.find((t) => t.TooltipTitle === title)?.TooltipMsg;
  const { Data, Load, Succeeded } = grantInfo || initialState.grantInfo;
  const data = Data || initialState.grantInfo.Data;

  const convertDate = (rawDate) => {
    return dayjs(rawDate).format("MM-DD-YYYY");
  };

  const convertCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const tableCols = useMemo(
    () => [
      {
        accessorKey: "TransactionType",
        header: "Transaction Type",
      },
      {
        accessorKey: "PaymentPostedDate",
        header: "Payment Posted Date",
        Cell: ({ cell }) => {
          const rawDate = cell.getValue();
          const formattedDate = convertDate(rawDate);
          return formattedDate;
        },
      },
      {
        accessorKey: "GrantAmount",
        header: "Amount",
        Cell: ({ cell }) => {
          const amount = cell.getValue();
          const formattedAmount = convertCurrency(amount);
          return formattedAmount;
        },
      },
      {
        accessorKey: "PaymentAmountReceived",
        header: "Payment Amount Received",
        Cell: ({ cell }) => {
          const amount = cell.getValue();
          const formattedAmount = convertCurrency(amount);
          return formattedAmount;
        },
      },
      {
        accessorKey: "DocumentType",
        header: "Document Type",
      },
      {
        accessorKey: "ReceivedDate",
        header: "Received Date",
        Cell: ({ cell }) => {
          const rawDate = cell.getValue();
          const formattedDate = convertDate(rawDate);
          return formattedDate;
        },
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns: tableCols,
    data: data.GrantTransactions || [],
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    muiTableBodyRowProps: { hover: false },
    enableGlobalFilter: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    muiTableProps: {
      sx: {
        backgroundColor: "transparent",
        caption: {
          captionSide: "top",
        },
      },
    },
    muiTableHeadCellProps: {
      sx: {
        border: "1px solid #ccc", // Add border here
        textAlign: "center !important",
        justifyContent: "center",
        fontWeight: "normal",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        textAlign: "center !important",
        border: "1px solid #ccc", // Add border here
        justifyContent: "center",
        backgroundColor: "transparent",
        fontWeight: "normal !important",
        color: "#495057",
      },
    },
    muiTableHeadRowProps: {
      sx: {
        background: "transparent !important",
      },
    },
  });

  return (
    <Box
      className="userInformationcontainer"
      sx={{ height: "100% !important" }}
    >
      <Accordion component="div" defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
          component="div"
        >
          <Typography component="h2" className="userInformation">
            Grant Information
            <InfoTooltip
              title="Grant Information"
              message={getTooltip("Grant Information")}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {Load.status === API_LOAD_STATUS.FULFILLED && Succeeded && (
            <>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                  <Typography component="div" className="UserName">
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                        <Typography component="span" className="textleft">
                          Heating Season
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                        <Typography component="span" className="textright">
                          {data.HeatingSeason || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Typography>

                  <Typography component="div" className="UserName">
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                        <Typography component="span" className="textleft">
                          Poverty Level
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                        <Typography component="span" className="textright">
                          {data.PovertyLevel || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Typography>

                  <Typography component="div" className="UserName">
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                        <Typography component="span" className="textleft">
                          Case Number
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                        <Typography component="span" className="textright">
                          {data.CaseNumber || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Typography>

                  <Typography component="div" className="UserName">
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                        <Typography component="span" className="textleft">
                          Residential Credit
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                        <Typography component="span" className="textright">
                          {data.ResumeEnrolled ? (
                            <CheckCircleOutlineIcon
                              sx={{
                                color: "#0A3B70",
                                fontSize: 16,
                              }}
                            />
                          ) : (
                            <CancelOutlinedIcon
                              className="redCancelIcon"
                              sx={{
                                fontSize: 16,
                              }}
                            />
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Typography>
                </Grid>
              </Grid>
              <Accordion component="div" defaultExpanded>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                  component="div"
                >
                  <Typography component="h2" className="userInformation">
                    Grant History
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    minHeight: "fit-content !important",
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid
                      size={{ xs: 12, sm: 12, md: 12 }}
                      className="Billingcontainer grantInformation"
                    >
                      <MaterialReactTable table={table} />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </>
          )}
          {Load.status === API_LOAD_STATUS.PENDING && (
            <Typography component="p" sx={{ textAlign: "center" }}>
              Loading Grant Info and History...
            </Typography>
          )}
          {(Load.status === API_LOAD_STATUS.REJECTED ||
            Load.status === API_LOAD_STATUS.FULFILLED) &&
            !Succeeded && (
              <Typography component="p" sx={{ textAlign: "center" }}>
                {Load.message || "No records to display."}
              </Typography>
            )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default GrantInformationAndHistory;
